import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useToastStore } from '@pkg/components/Toast/useToastStore';
import { type MutationAccountAddArgs, type MutationAccountVerifyArgs } from '@pkg/graphql/codegen/graphql';

import { type GraphQLError } from '@app/client';
import { callAddWallet, callRemoveWallet, callVerifyWallet, initializeWallets } from '@app/store/zustand';
import { USER_WALLETS_BY_USER_CACHE } from '@app/store/zustand/wallet/useWalletsQueryById';

export function useWalletMutations() {
    const queryClient = useQueryClient();
    const { addToast } = useToastStore();

    const invalidateUserAccounts = async () => {
        await queryClient.invalidateQueries({ queryKey: [USER_WALLETS_BY_USER_CACHE] });
        return await initializeWallets();
    };

    function handleOnError(error: GraphQLError) {
        const message = error.message;
        const isDuplicateError = message.includes('duplicate key value');
        const addedElsewhere = `accounts_verified_address_uk`;

        if (isDuplicateError) {
            addToast({
                message: `This wallet is already connected${message.includes(addedElsewhere) ? ' to another account.' : '.'}`,
                minimal: true,
            });
        } else {
            addToast({ message: `Something went wrong, please try again`, minimal: true });
        }
    }
    const { mutateAsync: verifyWallet } = useMutation({
        mutationFn: ({ variables }: { variables: MutationAccountVerifyArgs }) =>
            callVerifyWallet({ variables }).then((r) => {
                if (r?.error) {
                    handleOnError(r.error);
                }
                if (r?.accountVerify) {
                    invalidateUserAccounts();
                }
                return r;
            }),
    });

    const { mutateAsync: addWallet } = useMutation({
        mutationFn: ({ variables }: { variables: MutationAccountAddArgs }) =>
            callAddWallet({ variables }).then((r) => {
                if (r?.error) {
                    handleOnError(r.error);
                }
                return r;
            }),
        onError: handleOnError,
    });

    const { mutateAsync: removeWallet } = useMutation({
        mutationFn: (params: { accountAddress: string }) => callRemoveWallet({ variables: params }),
        onSuccess: invalidateUserAccounts,
    });

    return { addWallet, verifyWallet, removeWallet };
}
